import { AlertTriangle, X } from 'lucide-react';
import { useState } from 'react';

import { ptBR } from '@/i18n/pt-BR';
import type { ProportionWarningData } from '@/lib/case_workspace';
import { cn } from '@/lib/utils';

export interface ProportionWarningBannerProps {
  warning: ProportionWarningData | null;
  className?: string;
}

/**
 * Banner advisório de divergência de proporção entre crops (E15.F4). // ux-neutrality:allow doc-comment
 *
 * Não-bloqueante: apenas alerta o perito; o laudo segue normalmente. // ux-neutrality:allow doc-comment
 * Dismissível (estado local) e com render zero quando não há aviso ou // ux-neutrality:allow doc-comment
 * quando já foi dispensado. // ux-neutrality:allow doc-comment
 *
 * O dismiss vale para a montagem atual: a identidade do aviso é o caso, // ux-neutrality:allow doc-comment
 * então o chamador passa `key={caseId}` para remontar (e resetar o // ux-neutrality:allow doc-comment
 * dismiss) ao trocar de caso, preservando-o entre refreshes do mesmo // ux-neutrality:allow doc-comment
 * caso (F-E15.F4-01). // ux-neutrality:allow doc-comment
 */
export function ProportionWarningBanner({
  warning,
  className,
}: ProportionWarningBannerProps): JSX.Element | null {
  const [dismissed, setDismissed] = useState(false);
  if (!warning?.warned || dismissed) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="proportion-warning-banner"
      className={cn(
        'flex items-start gap-3 border border-warning/40 bg-warning-50 px-sc-4 py-sc-3 text-body-sm text-warning-700',
        className,
      )}
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div className="flex-1">
        <p className="font-medium">{ptBR.workspaceCaso.proportionWarningTitle}</p>
        <p className="mt-1">{ptBR.workspaceCaso.proportionWarningMessage}</p>
      </div>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        data-testid="proportion-warning-dismiss"
        aria-label={ptBR.workspaceCaso.proportionWarningDismiss}
        className="shrink-0 p-1 text-warning-700 transition-colors duration-fast ease-sc hover:bg-warning/20"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}
